import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, tap } from 'rxjs/operators';
import { of, Observable } from 'rxjs';
import { 
  AdminLoginGQL, 
  AdminMeGQL, 
  AdminUser, 
} from '../../graphql/generated';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly router = inject(Router);
  private readonly loginGQL = inject(AdminLoginGQL);
  private readonly meGQL = inject(AdminMeGQL);

  // State
  readonly currentUser = signal<AdminUser | null>(null);
  readonly isAuthenticated = computed(() => !!this.currentUser());
  readonly isLoading = signal(false);

  constructor() {
    this.restoreSession();
    this.checkSession();
  }

  private restoreSession() {
    if (typeof window === 'undefined') return;
    const storedUser = localStorage.getItem('auth_user');
    if (storedUser) {
      try {
        this.currentUser.set(JSON.parse(storedUser));
      } catch (e) {
        console.error('Failed to parse auth_user:', e);
      }
    }
  }

  login(idToken: string): Observable<boolean> {
    this.isLoading.set(true);
    return this.loginGQL.mutate({ 
      variables: { idToken }, 
      context: { clientName: 'admin' } 
    }).pipe(
      map(result => {
        const loginData = result.data?.login;
        if (loginData?.user) {
          const user = loginData.user as AdminUser;
          this.currentUser.set(user);
          localStorage.setItem('auth_token', loginData.token);
          localStorage.setItem('auth_user', JSON.stringify(user));
          return true;
        }
        return false;
      }),
      tap(() => this.isLoading.set(false)),
      catchError(error => {
        console.error('Login error:', error);
        this.isLoading.set(false);
        return of(false);
      })
    );
  }

  logout() {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    this.currentUser.set(null);
    this.router.navigate(['/admin/login']);
  }

  private checkSession() {
    if (typeof window === 'undefined') return;
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    this.isLoading.set(true);
    this.meGQL.fetch({ 
      fetchPolicy: 'network-only', 
      context: { clientName: 'admin' } 
    }).subscribe({
      next: (result) => {
        if (result.data?.me) {
          this.currentUser.set(result.data.me as AdminUser);
        } else {
          this.logout();
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.logout();
        this.isLoading.set(false);
      }
    });
  }
}
